import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Dto } from '@core/data/dto/dto';
import { BaseHttpService } from '@core/data/http/base-http.service';
import { DeliveriesByDriverReportRequest } from '@core/models/report/drivers/deliveries-by-driver-request';
import { ReportType } from '@core/models/report/report-type';
import { AuthService } from '@core/state/auth/auth.service';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

@Injectable()
export class DeliveriesDataService extends BaseHttpService {

    private urls: any = {
        deliveries: '/deliveries',
        delivery: '/deliveries/:orderId',
        deliveryConfig: '/deliveries/config/:locationId',
        deliveriesQuote: '/deliveries/quote',
        help: '/deliveries/dsp/help',
        searchIntegrations: '/deliveries/integrations/:locationId/search',
        integrations: '/deliveries/integrations/:locationId',
        integration: '/deliveries/integrations/:locationId/:id',
        refund: '/deliveries/:orderId/refund',
        dispatch: '/deliveries/:orderId/dispatch',
        redispatch: '/deliveries/:orderId/redispatch',
        tracking: '/deliveries/:orderId/tracking',
        reportDrivers: '/deliveries/reports/driver'
    };

    /**
     * Fetch a delivery.
     * @param orderId
     * @returns {Observable<Response>}
     */
    fetchDelivery(orderId: string): Observable<any> {
        return this.get(this.urls.delivery.replace(':orderId', orderId));
    }

    fetchDeliveryTracking(orderId: string): Observable<any> {
        return this.get(this.urls.tracking.replace(':orderId', orderId));
    }

    fetchDeliveriesQuote(order: Dto): Observable<any> {
        return this.post(
            this.urls.deliveriesQuote +
                this.toQueryParams({ driver_list: true, network_list: true }),
            order
        );
    }

    fetchDeliveryHelp(id: string): Observable<any> {
        return this.get(this.urls.help + this.toQueryParams({ delivery_id: id }));
    }

    createDelivery(dto): Observable<any> {
        return this.post(this.urls.deliveries, dto);
    }

    updateDelivery(orderId: string, dto): Observable<any> {
        return this.put(this.urls.delivery.replace(':orderId', orderId), dto);
    }

    cancelDelivery(orderId: string, params?: any): Observable<any> {
        let url: string = this.urls.delivery.replace(':orderId', orderId);

        if (params) {
            url += this.toQueryParams(params);
        }

        return this.delete(url);
    }

    dispatchDelivery(orderId: string, order: any): Observable<any> {
        let url: string = this.urls.dispatch.replace(':orderId', orderId);

        return this.put(url, order);
    }

    redispatchDelivery(orderId: string, order: any): Observable<any> {
        let url: string = this.urls.redispatch.replace(':orderId', orderId);

        return this.put(url, order);
    }

    fetchDeliveryConfig(locationId: string): Observable<any> {
        return this.get(
            this.urls.deliveryConfig.replace(':locationId', locationId)
        );
    }

    createDeliveryConfig(locationId: string, dto): Observable<any> {
        return this.post(
            this.urls.deliveryConfig.replace(':locationId', locationId),
            dto
        );
    }

    updateDeliveryConfig(locationId: string, dto): Observable<any> {
        return this.put(
            this.urls.deliveryConfig.replace(':locationId', locationId),
            dto
        );
    }

    requestRefund(orderId: string, dto) {
        return this.put(this.urls.refund.replace(':orderId', orderId), dto);
    }

    fetchCoverage(provider: string): Observable<any> {
        const coverageUrls: any = environment.ihd.coverage;
        return this.getResource(coverageUrls[provider]);
    }

    fetchDeliveryIntegrations(locationId: string): Observable<any> {
        return this.get( this.urls.searchIntegrations.replace(':locationId', locationId));
    }

    connectDeliveryIntegration(locationId: string, provider: string): Observable<any> {
        return this.post(this.urls.integrations.replace(':locationId', locationId), { provider });
    }

    disconnectDeliveryIntegration(locationId: string, id: string): Observable<any> {
        return this.delete(this.urls.integration.replace(':locationId', locationId).replace(':id', id));
    }

    fetchDeliveriesByDriverReport(request) {
        return this.post(this.urls.reportDrivers, request);
    }

    exportDeliveriesByDriverReport(request: DeliveriesByDriverReportRequest, reportType: ReportType) {
        
        let requestURL: string = this.urls.reportDrivers;

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/csv'),
            responseType: 'arraybuffer',
        };

        return this.post(requestURL, request, options);
    }
}
